import React, { useEffect, useRef } from 'react';

/**
 * ScrollReveal
 *
 * Available variants:
 * 'fade-up'
 * 'fade-left'
 * 'fade-right'
 * 'scale'
 * 'blur'
 * 'zoom'
 * 'flip'
 * 'cinematic'
 * 'magnetic'
 * 'glitch'
 * 'wave'
 * 'elastic'
 * 'mask'
 * 'perspective'
 * 'velocity'
 */

const ScrollReveal = ({
    children,
    variant = 'cinematic',
    delay = 0,
}) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        const targets = container.querySelectorAll(
            '.section-header, .about-story-card, .about-visual-card, .pillar-card, .corporate-service-card, .corporate-project-card, .corporate-tool-item, .fb-card, .fb-featured-card, .fb-summary-bar, .contact-card, .contact-info-panel, .evaluation-step-card, .evaluation-hero-card, .audit-card'
        );

        const elementsToObserve =
            targets.length > 0
                ? Array.from(targets)
                : [container];

        // -----------------------------------------
        // Initial Animation Styles
        // -----------------------------------------

        const getInitialStyles = () => {
            switch (variant) {
                // Simple upward reveal
                case 'fade-up':
                    return {
                        transform: 'translate3d(0, 50px, 0)',
                        filter: 'blur(3px)',
                    };

                // Left reveal
                case 'fade-left':
                    return {
                        transform: 'translate3d(-70px, 0, 0)',
                        filter: 'blur(4px)',
                    };

                // Right reveal
                case 'fade-right':
                    return {
                        transform: 'translate3d(70px, 0, 0)',
                        filter: 'blur(4px)',
                    };

                // Scale reveal
                case 'scale':
                    return {
                        transform: 'scale(0.82)',
                        filter: 'blur(5px)',
                    };

                // Blur reveal
                case 'blur':
                    return {
                        transform: 'translate3d(0, 35px, 0)',
                        filter: 'blur(15px)',
                    };

                // Zoom reveal
                case 'zoom':
                    return {
                        transform:
                            'translate3d(0, 45px, 0) scale(0.85)',
                        filter: 'blur(7px)',
                    };

                // 3D flip
                case 'flip':
                    return {
                        transform:
                            'perspective(1000px) rotateX(65deg) translateY(50px)',
                        filter: 'blur(5px)',
                    };

                // Premium cinematic
                case 'cinematic':
                    return {
                        transform:
                            'translate3d(0, 70px, 0) scale(0.94)',
                        filter: 'blur(9px)',
                    };

                // Magnetic
                case 'magnetic':
                    return {
                        transform:
                            'translate3d(0, 100px, 0) rotate(3deg) scale(0.92)',
                        filter: 'blur(6px)',
                    };

                // Glitch / tech
                case 'glitch':
                    return {
                        transform:
                            'translate3d(-20px, 25px, 0) skewX(8deg)',
                        filter: 'blur(8px)',
                    };

                // Wave
                case 'wave':
                    return {
                        transform:
                            'translate3d(0, 70px, 0) rotate(-4deg)',
                        filter: 'blur(5px)',
                    };

                // Elastic
                case 'elastic':
                    return {
                        transform:
                            'translate3d(0, 80px, 0) scale(0.75)',
                        filter: 'blur(4px)',
                    };

                // Mask reveal
                case 'mask':
                    return {
                        transform: 'translate3d(0, 50px, 0)',
                        clipPath: 'inset(100% 0 0 0)',
                        filter: 'blur(4px)',
                    };

                // 3D perspective
                case 'perspective':
                    return {
                        transform:
                            'perspective(1000px) rotateY(-35deg) translateX(-60px) scale(0.9)',
                        filter: 'blur(5px)',
                    };

                // Fast velocity
                case 'velocity':
                    return {
                        transform:
                            'translate3d(0, 150px, 0) scale(0.95)',
                        filter: 'blur(10px)',
                    };

                default:
                    return {
                        transform: 'translate3d(0, 50px, 0)',
                        filter: 'blur(4px)',
                    };
            }
        };

        // -----------------------------------------
        // Transition Settings
        // -----------------------------------------

        const getTransition = () => {
            switch (variant) {
                case 'elastic':
                    return `
            opacity 0.8s ease,
            transform 1.2s cubic-bezier(
              0.175,
              0.885,
              0.32,
              1.275
            ),
            filter 0.8s ease
          `;

                case 'velocity':
                    return `
            opacity 0.65s ease,
            transform 0.75s cubic-bezier(
              0.22,
              1,
              0.36,
              1
            ),
            filter 0.65s ease
          `;

                case 'mask':
                    return `
            opacity 0.9s ease,
            transform 0.9s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            ),
            filter 0.8s ease,
            clip-path 1s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            )
          `;

                case 'glitch':
                    return `
            opacity 0.7s ease,
            transform 0.75s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            ),
            filter 0.7s ease
          `;

                default:
                    return `
            opacity 0.9s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            ),
            transform 1s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            ),
            filter 0.9s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            ),
            clip-path 1s cubic-bezier(
              0.16,
              1,
              0.3,
              1
            )
          `;
            }
        };

        // -----------------------------------------
        // Apply Initial State
        // -----------------------------------------

        elementsToObserve.forEach((el) => {
            if (!el.dataset.srInitialized) {
                el.dataset.srInitialized = 'true';

                const initial = getInitialStyles();

                el.style.opacity = '0';
                el.style.transform = initial.transform;
                el.style.filter = initial.filter;

                if (initial.clipPath) {
                    el.style.clipPath = initial.clipPath;
                }

                el.style.transition = getTransition();

                el.style.willChange =
                    'opacity, transform, filter, clip-path';

                el.style.backfaceVisibility = 'hidden';
            }
        });

        // -----------------------------------------
        // Intersection Observer
        // -----------------------------------------

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    const el = entry.target;

                    // Find only reveal elements
                    const parent = el.parentNode;

                    const siblings = parent
                        ? Array.from(parent.children).filter(
                            (child) =>
                                elementsToObserve.includes(child)
                        )
                        : [];

                    const index = siblings.indexOf(el);

                    // Maximum stagger = 550ms
                    const staggerDelay =
                        Math.min(Math.max(index, 0) * 110, 550) +
                        delay;

                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            el.style.opacity = '1';

                            el.style.transform =
                                'translate3d(0, 0, 0) scale(1) rotate(0deg) rotateX(0deg) rotateY(0deg) skewX(0deg)';

                            el.style.filter = 'blur(0px)';

                            if (variant === 'mask') {
                                el.style.clipPath =
                                    'inset(0 0 0 0)';
                            }
                        });
                    }, staggerDelay);

                    // Animate only once
                    observer.unobserve(el);
                });
            },
            {
                threshold: 0.12,
                rootMargin: '0px 0px -60px 0px',
            }
        );

        elementsToObserve.forEach((el) => {
            observer.observe(el);
        });

        // -----------------------------------------
        // Cleanup
        // -----------------------------------------

        return () => {
            observer.disconnect();
        };
    }, [variant, delay]);

    return (
        <div
            ref={containerRef}
            style={{
                perspective: '1200px',
            }}
        >
            {children}
        </div>
    );
};

export default ScrollReveal;